import React, { useState } from "react";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import InventoryIcon from '@mui/icons-material/Inventory';

const InventarioGranelSection = ({
  granelRecords = [],
  isFormOpen,
  setIsFormOpen,
  fechaMovimiento,
  setFechaMovimiento,
  idContenedor,
  setIdContenedor,
  noLote,
  setNoLote,
  categoria,
  setCategoria,
  clase,
  setClase,
  ingrediente,
  setIngrediente,
  edad,
  setEdad,
  tipoMaguey,
  setTipoMaguey,
  noAnalisis,
  setNoAnalisis,
  invInicialVol,
  setInvInicialVol,
  invInicialAlc,
  setInvInicialAlc,
  entradaProcedencia,
  setEntradaProcedencia,
  entradaVolumen,
  setEntradaVolumen,
  entradaAlc,
  setEntradaAlc,
  entradaVolAgua,
  setEntradaVolAgua,
  salidaVolumen,
  setSalidaVolumen,
  salidaAlc,
  setSalidaAlc,
  salidaDestino,
  setSalidaDestino,
  invFinalVol,
  setInvFinalVol,
  invFinalAlc,
  setInvFinalAlc,
  observaciones,
  setObservaciones,
  handleSaveGranel,
  handleEditRecord
}) => {
  // Modal de Detalle
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Modal de Fusión / Mezcla de Lotes de Granel
  const [isFusionModalOpen, setIsFusionModalOpen] = useState(false);
  const [fusionSources, setFusionSources] = useState([
    { granelId: "", volExtraer: "" },
    { granelId: "", volExtraer: "" }
  ]);
  const [fusionNuevoLote, setFusionNuevoLote] = useState("");
  const [fusionNuevoContenedor, setFusionNuevoContenedor] = useState("");
  const [fusionObservaciones, setFusionObservaciones] = useState("");

  // Cálculo automático del Inventario Final cuando cambian las entradas o salidas
  const recalculateInvFinal = (initV, entV, aguaV, salV) => {
    const init = parseFloat(initV || 0);
    const ent = parseFloat(entV || 0);
    const agua = parseFloat(aguaV || 0);
    const sal = parseFloat(salV || 0);
    const finalCalculado = Math.max(0, (init + ent + agua) - sal);
    setInvFinalVol(finalCalculado.toString());
  };

  // Manejadores para Mezcla / Fusión
  const handleAddFusionSource = () => {
    setFusionSources(prev => [...prev, { granelId: "", volExtraer: "" }]);
  };

  const handleRemoveFusionSource = (idx) => {
    if (fusionSources.length <= 2) {
      alert("Una mezcla o fusión debe requerir al menos 2 lotes origen.");
      return;
    }
    setFusionSources(prev => prev.filter((_, i) => i !== idx));
  };

  const handleFusionSourceChange = (idx, field, value) => {
    setFusionSources(prev => {
      const updated = [...prev];
      updated[idx][field] = value;
      return updated;
    });
  };

  const calculateTotalFusionVolume = () => {
    return fusionSources.reduce((sum, item) => sum + (parseFloat(item.volExtraer) || 0), 0);
  };

  const handleConfirmFusion = () => {
    if (!fusionNuevoLote.trim() || !fusionNuevoContenedor.trim()) {
      alert("Por favor ingrese el nuevo Número de Lote y el ID del Contenedor de Destino.");
      return;
    }

    const totalVol = calculateTotalFusionVolume();
    if (totalVol <= 0) {
      alert("Por favor ingrese un volumen a extraer mayor a 0 litros para cada lote origen seleccionado.");
      return;
    }

    let weightedAlcSum = 0;
    let validSources = 0;

    for (let src of fusionSources) {
      if (!src.granelId) {
        alert("Asegúrese de haber seleccionado un Lote Origen en todas las filas de la mezcla.");
        return;
      }
      const granelItem = granelRecords.find(g => (g.id || g.noLote) === src.granelId || g.noLote === src.granelId);
      if (granelItem) {
        const entVol = parseFloat(granelItem.entradaVolumen || granelItem.invInicialVol || 0);
        const salVol = parseFloat(granelItem.salidaVolumen || 0);
        const volDisp = granelItem.invFinalVol !== undefined ? parseFloat(granelItem.invFinalVol) : Math.max(0, entVol - salVol);
        const volReq = parseFloat(src.volExtraer || 0);

        if (volReq > volDisp + 0.01) {
          alert(`El volumen a extraer (${volReq} L) supera el saldo disponible (${volDisp} L) en el lote ${granelItem.noLote}.`);
          return;
        }

        const pctAlc = parseFloat(granelItem.invFinalAlc || granelItem.entradaAlc || granelItem.invInicialAlc || 0);
        weightedAlcSum += (pctAlc * volReq);
        validSources++;
      }
    }

    const avgAlc = totalVol > 0 ? (weightedAlcSum / totalVol).toFixed(1) : "0.0";
    const firstGranel = granelRecords.find(g => (g.id || g.noLote) === fusionSources[0].granelId) || {};

    // Invocar guardado con el nuevo registro de mezcla
    setFechaMovimiento(new Date().toISOString().split('T')[0]);
    setIdContenedor(fusionNuevoContenedor);
    setNoLote(fusionNuevoLote);
    setCategoria(firstGranel.categoria || "Mezcal Artesanal");
    setClase(firstGranel.clase || "Joven / Blanco");
    setIngrediente(firstGranel.ingrediente || firstGranel.ingredientes || "Ensamble de Graneles");
    setEdad(firstGranel.edad || "Sin edad");
    setTipoMaguey(firstGranel.tipoMaguey || "Ensamble");
    setNoAnalisis(firstGranel.noAnalisis || "AN-MEZCLA-01");
    setInvInicialVol(totalVol.toString());
    setInvInicialAlc(avgAlc.toString());
    setEntradaProcedencia(`Fusión / Mezcla de Lotes (${validSources} orígenes)`);
    setEntradaVolumen("0");
    setEntradaAlc(avgAlc.toString());
    setEntradaVolAgua("0");
    setSalidaVolumen("0");
    setSalidaAlc("0.0");
    setSalidaDestino("N/A");
    setInvFinalVol(totalVol.toString());
    setInvFinalAlc(avgAlc.toString());
    setObservaciones(fusionObservaciones || `Mezcla / Fusión consolidada de ${totalVol} Litros.`);

    handleSaveGranel("revision");

    setIsFusionModalOpen(false);
    setFusionSources([{ granelId: "", volExtraer: "" }, { granelId: "", volExtraer: "" }]);
    setFusionNuevoLote("");
    setFusionNuevoContenedor("");
    setFusionObservaciones("");
    alert(`¡Fusión de Graneles registrada exitosamente! Nuevo Lote: ${fusionNuevoLote}`);
  };

  // VISTA 1: LISTADO DE REGISTROS (TABLA HISTORIAL)
  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 style={{ margin: 0 }}>Inventarios de Mezcal a Granel (Sección II)</h2>
            <small style={{ color: "var(--text-muted)" }}>Control trazable de contenedores, entradas, salidas y fusiones de mezcal a granel</small>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button 
              onClick={() => setIsFusionModalOpen(true)}
              className="btn-secondary-custom"
              style={{ display: "flex", alignItems: "center", gap: "8px", background: "rgba(155, 89, 182, 0.15)", border: "1px solid rgba(155, 89, 182, 0.4)", color: "#d5a6bd" }}
            >
              <MergeTypeIcon style={{ fontSize: 18, color: "#9b59b6" }} />
              <span>Mezclar / Juntar Graneles</span>
            </button>
            
            <button 
              onClick={() => setIsFormOpen(true)}
              className="btn-add-new"
            >
              <AddIcon style={{ fontSize: 18 }} />
              <span>Nuevo Registro de Granel</span>
            </button>
          </div>
        </div>

        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th colSpan="9" style={{ textAlign: "center", background: "rgba(74, 93, 35, 0.2)", color: "var(--agave-green)" }}>Datos Generales / Identificación del Mezcal</th>
                <th colSpan="2" style={{ textAlign: "center", background: "rgba(210, 105, 30, 0.2)", color: "var(--copper)" }}>Inventario Inicial</th>
                <th colSpan="4" style={{ textAlign: "center", background: "rgba(74, 93, 35, 0.2)", color: "var(--agave-green)" }}>Entradas</th>
                <th colSpan="3" style={{ textAlign: "center", background: "rgba(210, 105, 30, 0.2)", color: "var(--copper)" }}>Salidas</th>
                <th colSpan="2" style={{ textAlign: "center", background: "rgba(74, 93, 35, 0.2)", color: "var(--agave-green)" }}>Inventario Final</th>
                <th colSpan="4" style={{ textAlign: "center" }}>Acciones e Inspección</th>
              </tr>
              <tr>
                {/* Identificación */}
                <th>Fecha Mov.</th>
                <th>Contenedor</th>
                <th>Nº Lote</th>
                <th>Categoría</th>
                <th>Clase</th>
                <th>Ingrediente</th>
                <th>Edad</th>
                <th>Tipo Maguey</th>
                <th>Nº Análisis</th>

                {/* Inv Inicial */}
                <th>Volumen (L)</th>
                <th>% Alc. Vol.</th>

                {/* Entradas */}
                <th>Procedencia</th>
                <th>Volumen (L)</th>
                <th>% Alc. Vol.</th>
                <th>Vol. Agua (L)</th>

                {/* Salidas */}
                <th>Volumen (L)</th>
                <th>% Alc. Vol.</th>
                <th>Destino</th>

                {/* Inv Final */}
                <th>Volumen (L)</th>
                <th>% Alc. Vol.</th>

                {/* Cierre */}
                <th>Observaciones</th>
                <th>V.Bo. Inspector</th>
                <th>Estado</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {granelRecords.length === 0 ? (
                <tr>
                  <td colSpan="24" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                    <InventoryIcon style={{ fontSize: 40, opacity: 0.4, display: "block", margin: "0 auto 10px auto" }} />
                    No hay registros de inventario de granel. Haga clic en "Nuevo Registro de Granel" para agregar uno.
                  </td>
                </tr>
              ) : (
                granelRecords.map((rec, i) => (
                  <tr key={i}>
                    <td>{rec.fechaMovimiento}</td>
                    <td><span className="badge-tech" style={{ background: "rgba(255,255,255,0.06)" }}>{rec.idContenedor}</span></td>
                    <td style={{ fontWeight: 800, color: "var(--agave-green)" }}>{rec.noLote}</td>
                    <td>{rec.categoria}</td>
                    <td>{rec.clase}</td>
                    <td>{rec.ingrediente || rec.ingredientes || "-"}</td>
                    <td>{rec.edad || "-"}</td>
                    <td>{rec.tipoMaguey}</td>
                    <td>{rec.noAnalisis || "-"}</td>

                    <td>{rec.invInicialVol} L</td>
                    <td>{rec.invInicialAlc}%</td>

                    <td>{rec.entradaProcedencia || "-"}</td>
                    <td>{rec.entradaVolumen} L</td>
                    <td>{rec.entradaAlc}%</td>
                    <td style={{ color: "var(--copper)" }}>{rec.entradaVolAgua || 0} L</td>

                    <td>{rec.salidaVolumen} L</td>
                    <td>{rec.salidaAlc}%</td>
                    <td>{rec.salidaDestino || "-"}</td>

                    <td style={{ color: "var(--agave-green)", fontWeight: 700 }}>{rec.invFinalVol} L</td>
                    <td>{rec.invFinalAlc}%</td>

                    <td style={{ maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {rec.observaciones}
                    </td>
                    <td style={{ fontSize: "11px", color: "var(--gold-accent)" }}>{rec.inspector || "Pendiente"}</td>
                    <td>
                      <span className={`badge-status ${rec.status || 'borrador'}`}>
                        {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : rec.status === "rechazado" ? "Rechazado" : "Borrador"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver detalle completo"
                          style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: "6px", padding: "6px", color: "#fff", cursor: "pointer" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>
                        {handleEditRecord && (rec.status === "borrador" || rec.status === "rechazado") && (
                          <button
                            onClick={() => handleEditRecord(rec)}
                            title="Editar registro"
                            style={{ background: "rgba(210, 105, 30, 0.2)", border: "none", borderRadius: "6px", padding: "6px", color: "var(--copper)", cursor: "pointer" }}
                          >
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL DETALLE DE REGISTRO */}
        {selectedRecord && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: "20px" }}>
            <div className="detail-card animate-fadeIn" style={{ maxWidth: "650px", width: "100%", maxHeight: "90vh", overflowY: "auto", border: "1px solid var(--agave-green)", borderRadius: "16px", padding: "24px", position: "relative" }}>
              <button
                onClick={() => setSelectedRecord(null)}
                style={{ position: "absolute", top: "16px", right: "16px", background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <CloseIcon />
              </button>
              
              <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px", marginBottom: "16px" }}>
                <LayersIcon style={{ color: "var(--agave-green)", fontSize: 28 }} />
                <div>
                  <h3 style={{ margin: 0, color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>Detalle de Lote Granel: {selectedRecord.noLote}</h3>
                  <small style={{ color: "var(--text-muted)" }}>Contenedor: {selectedRecord.idContenedor} | Movimiento del {selectedRecord.fechaMovimiento}</small>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "14px" }}>
                <div><strong>Categoría:</strong> {selectedRecord.categoria}</div>
                <div><strong>Clase:</strong> {selectedRecord.clase}</div>
                <div><strong>Especie de Maguey:</strong> {selectedRecord.tipoMaguey}</div>
                <div><strong>Nº Análisis Fisicoquímico:</strong> {selectedRecord.noAnalisis || "N/A"}</div>
                <div><strong>Ingrediente(s):</strong> {selectedRecord.ingrediente || selectedRecord.ingredientes || "N/A"}</div>
                <div><strong>Edad:</strong> {selectedRecord.edad || "Sin edad"}</div>

                <div style={{ gridColumn: "1 / -1", marginTop: "10px", padding: "12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h4 style={{ margin: "0 0 8px 0", color: "var(--copper)" }}>Saldos e Inventarios</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                    <div><strong>Inv. Inicial:</strong> {selectedRecord.invInicialVol} L ({selectedRecord.invInicialAlc}%)</div>
                    <div><strong>Entradas:</strong> {selectedRecord.entradaVolumen} L (+{selectedRecord.entradaVolAgua || 0}L Agua)</div>
                    <div><strong>Salidas:</strong> {selectedRecord.salidaVolumen} L ({selectedRecord.salidaDestino || "N/A"})</div>
                  </div>
                  <div style={{ marginTop: "8px", fontWeight: "bold", color: "var(--agave-green)" }}>
                    <strong>Saldo Final Resultante:</strong> {selectedRecord.invFinalVol} Litros ({selectedRecord.invFinalAlc} % Alc. Vol.)
                  </div>
                </div>

                {selectedRecord.observaciones && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <strong>Observaciones:</strong>
                    <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontStyle: "italic" }}>{selectedRecord.observaciones}</p>
                  </div>
                )}

                <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "12px", marginTop: "8px" }}>
                  <span><strong>V.Bo. Inspector:</strong> {selectedRecord.inspector || "Pendiente"}</span>
                  <span className={`badge-status ${selectedRecord.status}`}>{selectedRecord.status}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL FUSION / MEZCLA DE GRANELES */}
        {isFusionModalOpen && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: "20px" }}>
            <div className="detail-card animate-fadeIn" style={{ maxWidth: "700px", width: "100%", maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(155, 89, 182, 0.4)", borderRadius: "16px", padding: "24px", position: "relative" }}>
              <button
                onClick={() => setIsFusionModalOpen(false)}
                style={{ position: "absolute", top: "16px", right: "16px", background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <CloseIcon />
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px", marginBottom: "16px" }}>
                <MergeTypeIcon style={{ color: "#9b59b6", fontSize: 28 }} />
                <div>
                  <h3 style={{ margin: 0, color: "#9b59b6", fontSize: "18px", fontWeight: 800 }}>Fusión / Mezcla de Lotes de Granel</h3>
                  <small style={{ color: "var(--text-muted)" }}>Seleccione 2 o más lotes existentes para extraer y consolidar en un nuevo registro</small>
                </div>
              </div>

              {fusionSources.map((src, idx) => (
                <div key={idx} style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px", padding: "10px", borderRadius: "10px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Granel Origen #{idx + 1}</label>
                    <select
                      value={src.granelId}
                      onChange={e => handleFusionSourceChange(idx, "granelId", e.target.value)}
                      className="custom-select-field"
                      style={{ padding: "10px", marginTop: "4px" }}
                    >
                      <option value="">-- Seleccionar Lote Granel Origen --</option>
                      {granelRecords.map((g, gIdx) => {
                        const entVol = parseFloat(g.entradaVolumen || g.invInicialVol || 0);
                        const salVol = parseFloat(g.salidaVolumen || 0);
                        const volDisp = g.invFinalVol !== undefined ? parseFloat(g.invFinalVol) : Math.max(0, entVol - salVol);
                        return (
                          <option key={gIdx} value={g.id || g.noLote}>
                            {g.noLote} | {g.idContenedor} ({volDisp} L disp.)
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "12px", color: "var(--text-muted)" }}>Vol. Extraer (L)</label>
                    <input
                      type="number"
                      placeholder="0.0"
                      value={src.volExtraer}
                      onChange={e => handleFusionSourceChange(idx, "volExtraer", e.target.value)}
                      className="custom-input-field"
                      style={{ padding: "10px", marginTop: "4px" }}
                    />
                  </div>
                  {fusionSources.length > 2 && (
                    <button
                      onClick={() => handleRemoveFusionSource(idx)}
                      style={{ background: "rgba(231, 76, 60, 0.2)", border: "none", color: "#e74c3c", borderRadius: "6px", padding: "8px", marginTop: "18px", cursor: "pointer" }}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={handleAddFusionSource}
                style={{ background: "transparent", border: "1px dashed rgba(155, 89, 182, 0.5)", color: "#d5a6bd", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", width: "100%", marginBottom: "16px" }}
              >
                + Agregar otro Lote Origen a la Mezcla
              </button>

              <div style={{ padding: "12px", background: "rgba(155, 89, 182, 0.1)", borderRadius: "10px", marginBottom: "16px" }}>
                <strong>Volumen Resultante de Mezcla:</strong> <span style={{ color: "#9b59b6", fontSize: "18px", fontWeight: 800 }}>{calculateTotalFusionVolume()} Litros</span>
              </div>

              <div className="species-grid" style={{ marginBottom: "12px" }}>
                <div className="custom-input-group">
                  <input
                    type="text"
                    value={fusionNuevoLote}
                    onChange={e => setFusionNuevoLote(e.target.value)}
                    className="custom-input-field"
                    placeholder="Ej. L-MEZCLA-01"
                  />
                  <label className="custom-input-label">Nuevo Nº de Lote Resultante</label>
                </div>
                <div className="custom-input-group">
                  <input
                    type="text"
                    value={fusionNuevoContenedor}
                    onChange={e => setFusionNuevoContenedor(e.target.value)}
                    className="custom-input-field"
                    placeholder="Ej. C-MEZCLA-01"
                  />
                  <label className="custom-input-label">ID Contenedor Destino</label>
                </div>
              </div>

              <div className="custom-input-group" style={{ marginBottom: "20px" }}>
                <textarea
                  value={fusionObservaciones}
                  onChange={e => setFusionObservaciones(e.target.value)}
                  className="custom-input-field"
                  placeholder="Justificación o detalles técnicos de la mezcla..."
                  style={{ height: "70px", resize: "none" }}
                />
                <label className="custom-input-label">Observaciones de la Fusión</label>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button onClick={() => setIsFusionModalOpen(false)} className="btn-secondary-custom">Cancelar</button>
                <button onClick={handleConfirmFusion} className="btn-primary-custom" style={{ background: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)" }}>
                  <MergeTypeIcon style={{ fontSize: 16 }} />
                  <span>Confirmar Fusión de Lotes</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // VISTA 2: FORMULARIO DE CAPTURA / EDICIÓN
  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Captura de Mezcal a Granel - Sección II</h2>
      </div>

      <div className="cards-container-grid">
        {/* SECCIÓN IDENTIFICACIÓN */}
        <section className="detail-card detail-card-accent-left-green">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <LayersIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Identificación del Mezcal</h2>
              <p>Datos de lote, contenedor y clasificación comercial</p>
            </div>
          </header>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="date" 
                id="fechaMovimiento" 
                value={fechaMovimiento} 
                onChange={e => setFechaMovimiento(e.target.value)} 
                className="custom-input-field"
              />
              <label className="custom-input-label" htmlFor="fechaMovimiento">Fecha del Movimiento</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                id="idContenedor" 
                value={idContenedor} 
                onChange={e => setIdContenedor(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. C-01 / Tina 04"
              />
              <label className="custom-input-label" htmlFor="idContenedor">ID Contenedor / Tanque</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                id="noLote" 
                value={noLote} 
                onChange={e => setNoLote(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. L-01-ENV"
              />
              <label className="custom-input-label" htmlFor="noLote">Nº de Lote</label>
            </div>

            <div className="custom-input-group">
              <select 
                value={categoria} 
                onChange={e => setCategoria(e.target.value)}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px" }}
              >
                <option value="Mezcal">Mezcal</option>
                <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                <option value="Mezcal Ancestral">Mezcal Ancestral</option>
              </select>
              <label className="custom-input-label">Categoría</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <select 
                value={clase} 
                onChange={e => setClase(e.target.value)}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px" }}
              >
                <option value="Joven / Blanco">Joven / Blanco</option>
                <option value="Madurado en Vidrio">Madurado en Vidrio</option>
                <option value="Reposado">Reposado</option>
                <option value="Añejo">Añejo</option>
                <option value="Abocado con">Abocado con</option>
                <option value="Destilado con">Destilado con</option>
              </select>
              <label className="custom-input-label">Clase</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                id="ingrediente" 
                value={ingrediente} 
                onChange={e => setIngrediente(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. Maguey Espadín / Gusano"
              />
              <label className="custom-input-label" htmlFor="ingrediente">Ingrediente(s)</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                id="edad" 
                value={edad} 
                onChange={e => setEdad(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. Sin edad / 2 meses"
              />
              <label className="custom-input-label" htmlFor="edad">Edad (meses o años)</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                id="tipoMaguey" 
                value={tipoMaguey} 
                onChange={e => setTipoMaguey(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. Espadín"
              />
              <label className="custom-input-label" htmlFor="tipoMaguey">Tipo de Maguey(es)</label>
            </div>
          </div>

          <div className="custom-input-group" style={{ marginTop: 12 }}>
            <input 
              type="text" 
              id="noAnalisis" 
              value={noAnalisis} 
              onChange={e => setNoAnalisis(e.target.value)} 
              className="custom-input-field"
              placeholder="Ej. ANA-2026-98"
            />
            <label className="custom-input-label" htmlFor="noAnalisis">Nº de Análisis Fisicoquímico</label>
          </div>
        </section>

        {/* INVENTARIO INICIAL & ENTRADAS */}
        <section className="detail-card detail-card-accent-left-copper">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-copper">
              <ScienceIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Inicial & Entradas</h2>
              <p>Volumen de inicio, procedencias y ajuste con agua</p>
            </div>
          </header>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-color)", marginBottom: "8px" }}>INVENTARIO INICIAL</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="number" 
                value={invInicialVol} 
                onChange={e => {
                  setInvInicialVol(e.target.value);
                  recalculateInvFinal(e.target.value, entradaVolumen, entradaVolAgua, salidaVolumen);
                }} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">Volumen Inicial (L)</label>
              <span className="input-suffix">L</span>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                step="0.1"
                value={invInicialAlc} 
                onChange={e => setInvInicialAlc(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">% Alc. Vol. Inicial</label>
              <span className="input-suffix">%</span>
            </div>
          </div>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", marginBottom: "8px", marginTop: "16px" }}>ENTRADAS</h3>
          <div className="custom-input-group">
            <input 
              type="text" 
              value={entradaProcedencia} 
              onChange={e => setEntradaProcedencia(e.target.value)} 
              className="custom-input-field" 
              placeholder="Ej. Producción Lote 01 / Compra / Trasvase"
            />
            <label className="custom-input-label">Procedencia</label>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="number" 
                value={entradaVolumen} 
                onChange={e => {
                  setEntradaVolumen(e.target.value);
                  recalculateInvFinal(invInicialVol, e.target.value, entradaVolAgua, salidaVolumen);
                }} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">Volumen Entrada (L)</label>
              <span className="input-suffix">L</span>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                step="0.1"
                value={entradaAlc} 
                onChange={e => setEntradaAlc(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">% Alc. Vol.</label>
              <span className="input-suffix">%</span>
            </div>
          </div>

          <div className="custom-input-group" style={{ marginTop: 12 }}>
            <input 
              type="number" 
              value={entradaVolAgua} 
              onChange={e => {
                setEntradaVolAgua(e.target.value);
                recalculateInvFinal(invInicialVol, entradaVolumen, e.target.value, salidaVolumen);
              }} 
              className="custom-input-field" 
            />
            <label className="custom-input-label">Volumen Agua para ajuste (L)</label>
            <span className="input-suffix">L</span>
          </div>
        </section>

        {/* SALIDAS & INVENTARIO FINAL */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1" }}>
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <WaterDropIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Salidas e Inventario Final</h2>
              <p>Desglose de salidas de granel y saldos de cierre</p>
            </div>
          </header>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-color)", marginBottom: "8px" }}>SALIDAS</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="number" 
                value={salidaVolumen} 
                onChange={e => {
                  setSalidaVolumen(e.target.value);
                  recalculateInvFinal(invInicialVol, entradaVolumen, entradaVolAgua, e.target.value);
                }} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">Volumen Salida (L)</label>
              <span className="input-suffix">L</span>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                step="0.1"
                value={salidaAlc} 
                onChange={e => setSalidaAlc(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">% Alc. Vol. Salida</label>
              <span className="input-suffix">%</span>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                value={salidaDestino} 
                onChange={e => setSalidaDestino(e.target.value)} 
                className="custom-input-field" 
                placeholder="Ej. Línea 1 - Envasado Botella 750ml"
              />
              <label className="custom-input-label">Destino de Salida</label>
            </div>
          </div>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", marginBottom: "8px", marginTop: "16px" }}>INVENTARIO FINAL CALCULADO</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="number" 
                value={invFinalVol} 
                onChange={e => setInvFinalVol(e.target.value)} 
                className="custom-input-field" 
                style={{ fontWeight: "bold", color: "var(--agave-green)" }}
              />
              <label className="custom-input-label">Volumen Final Resultante (L)</label>
              <span className="input-suffix">L</span>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                step="0.1"
                value={invFinalAlc} 
                onChange={e => setInvFinalAlc(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">% Alc. Vol. Final Saldo</label>
              <span className="input-suffix">%</span>
            </div>
          </div>

          <div className="custom-input-group" style={{ marginTop: 20 }}>
            <textarea 
              id="observaciones" 
              value={observaciones} 
              onChange={e => setObservaciones(e.target.value)} 
              className="custom-input-field"
              style={{ height: "80px", resize: "none", padding: "14px" }}
              placeholder="Notas generales del movimiento de granel..."
            />
            <label className="custom-input-label" htmlFor="observaciones">Observaciones</label>
          </div>
        </section>
      </div>

      <div className="button-row" style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)}>Cancelar</button>
        <button 
          type="button" 
          className="btn-secondary-custom" 
          onClick={() => handleSaveGranel("borrador")}
          style={{ background: "rgba(255,255,255,0.06)" }}
        >
          Guardar Borrador
        </button>
        <button 
          type="button" 
          className="btn-primary-custom" 
          onClick={() => handleSaveGranel("revision")}
        >
          <CheckCircleIcon style={{ fontSize: 16 }} />
          <span>Registrar Sección II</span>
        </button>
      </div>
    </div>
  );
};

export default InventarioGranelSection;
